export interface BaseNodeItem<T> {
	children?: T[];
}

export type ChildrenLoader<T> = (node: T) => Promise<T[]>;

export interface TreeNodeOptions<T> {
	childrenLoader?: ChildrenLoader<T>;
	parent?: T;
	children?: T[];
}
export abstract class TreeNode<T extends BaseNodeItem<T>> {
	parent?: this;
	abstract get id(): string;
	abstract get title(): string;
	visible = true;
	expanded = false;

	private _selected = false;
	public get selected(): boolean {
		return this._selected || false;
	}
	public set selected(value: boolean) {
		this._selected = value;
	}
	onSelect?(value: boolean): void;
	unselectable = false;

	children?: this[];
	childrenLoading = false;
	childrenPromise?: Promise<this[]>;

	get showButtons() {
		return false;
	}
	onAdd?(): void;
	onEdit?(): void;
	onDelete?(): void;

	private _favorite?: boolean;
	public get favorite(): boolean {
		return this._favorite || false;
	}
	public set favorite(value: boolean) {
		this._favorite = value;
	}
	onFavClick?(): void;

	childrenLoader?: ChildrenLoader<this>;

	constructor(readonly item: T) {}

	initTree(options: TreeNodeOptions<this>) {
		if (options.children) {
			this.children = options.children;
		} else if (options.childrenLoader) {
			this.childrenLoader = options.childrenLoader;
		} else {
			this.children = [];
		}
		this.parent = options.parent;
	}

	get childrenLoaded() {
		return Array.isArray(this.children);
	}
	get hasChildren() {
		return this.childrenLoaded && this.children && this.children.length > 0;
	}

	async loadChildren(): Promise<this[]> {
		if (this.childrenLoaded) {
			return this.children as this[];
		}
		if (this.childrenLoader) {
			this.childrenLoading = true;
			this.childrenPromise = this.childrenPromise || this.childrenLoader(this);
			this.childrenLoading = false;
			this.children = await this.childrenPromise;
		} else {
			this.children = [];
		}

		return this.children;
	}

	async toggleExpanded() {
		this.expanded = !this.expanded;
		if (this.expanded) {
			await this.loadChildren();
			await Promise.all(this.children?.map((child) => child.loadChildren()) || []);
		}
	}

	async selectByPath(path: string): Promise<TreeNode<T> | undefined> {
		const ids = path.split('_');

		const selectChildByPath = async (node: TreeNode<T>, ids: string[]): Promise<TreeNode<T> | undefined> => {
			await node.loadChildren();

			const isLeaf = ids.length === 1;
			const id = ids.pop();

			const child = node.children?.find((child) => child.id === id);
			if (!child) {
				return;
			}

			if (isLeaf) {
				child.selected = true;
				await child.loadChildren();
				return child;
			} else {
				child.expanded = true;
				return await selectChildByPath(child, ids);
			}
		};

		return await selectChildByPath(this, ids);
	}

	clearSelection() {
		doForAllChildren(this.children, (child) => (child.selected = false));
	}

	getSelected() {
		const selected: TreeNode<T>[] = [];
		doForAllChildren(this.children, (child) => child.selected && selected.push(child));
		return selected;
	}

	hasSelected() {
		return this.getSelected().length > 0;
	}
}

function doForAllChildren<T extends BaseNodeItem<T>>(nodes: TreeNode<T>[] = [], cb: (child: TreeNode<T>) => void) {
	nodes.forEach((node) => {
		cb(node);
		doForAllChildren(node.children, cb);
	});
}
